/**
 * Format a fieldName as it should be used in tracking
 * @param fieldName {string}
 * @return {string}
 */
export function trackingFieldName(fieldName) {
	if (!fieldName) return undefined;
	return fieldName
		.split(/(?=[A-Z])/)
		.map((a) => a.charAt(0).toUpperCase() + a.substr(1))
		.join(" ");
}
/**
 * Validate a field value against the validation rules
 * @param fieldValue {string|boolean|number|undefined}
 * @param options {{maxLength?: number, validatePattern?: string | RegExp, required?: boolean, apiValidationError?: string, allowPatterns?: Array<any>, disallowPatterns?: Array<any>}}
 * @return {{formatError: boolean, isValid: boolean, maxLengthError: boolean, requiredError: boolean, apiValidationError: string}}
 */
export function validateFieldState(
	fieldValue,
	{ maxLength, validatePattern, required, apiValidationError, allowPatterns, disallowPatterns } = {}
) {
	// trim if the value is string to prevent submitting spaces
	const value = typeof fieldValue === "string" ? fieldValue.trim() : fieldValue;

	const requiredError = required ? !value : false;
	const maxLengthError = maxLength && value && typeof value !== 'boolean' ? value?.length > maxLength : false;
	const hasValidatePattern =
		typeof validatePattern !== "undefined" &&
		validatePattern !== null &&
		validatePattern !== "";

	allowPatterns = allowPatterns || [];
	disallowPatterns = disallowPatterns || [];
	// check if both the allowPatterns and disallowPatterns arrays empty and
	// if we have a normal validatePattern
	const hasAllowPatterns = allowPatterns.length > 0;
	const hasDisallowPatterns = disallowPatterns.length > 0;
	if (!hasAllowPatterns && !hasDisallowPatterns && hasValidatePattern) {
		// in this case we will push the simple regex to the allowPatterns array
		allowPatterns = [{
			validationRegExpr: validatePattern,
		}];
	}
	const performValidation = hasAllowPatterns || hasDisallowPatterns || hasValidatePattern;

	const validateDisallowPatternsResult = validateDisallowPatterns(String(value).toLowerCase(), disallowPatterns);

	const formatError = performValidation && value
		? !(
			validateAllowPatterns(String(value).toLowerCase(), allowPatterns) &&
			validateDisallowPatternsResult.result
		)
		: false;


	return {
		requiredError,
		maxLengthError,
		formatError,
		formatErrorMessage: validateDisallowPatternsResult.validationErrorMessage || '',
		apiValidationError,
		isValid: !(
			requiredError ||
			maxLengthError ||
			formatError ||
			!!apiValidationError
		),
	};
}

/**
 * Formats the API error messages into a structured format.
 * @param {string} messages - A string containing error messages separated by line breaks.
 * @returns {Array<{name: string, fieldName: string, message: string}>} An array of objects with name, fieldName, and message properties.
 * @example formatApiMessages("[DeliveryInformation.BillingAddress.Title, Invalid selection ]")
 */
export function formatApiMessages(messages) {
	if (!messages) return [];

	const lines = messages.split("\r\n");

	return lines.map((line, index) => {
		const [name, message] = line
			.replace("[", "")
			.replace("]", "")
			.split(",");

		const field = document.querySelector(
			`input[name="${name}"], select[name="${name}"]`
		);
		const fieldName = field?.dataset.fieldName;

		if (fieldName && index === 0) {
			// Ensure focus is set to the field. If we want to to make this function more clean, we could move these sideeffects out of the code
			focusOnField(fieldName);
		}

		// Ensure we always return a "truthy" string, so our validation correctly detects it.
		return { name, fieldName, message: typeof message === 'string' && message !== "" ? message.trim() : " " };
	});
}

/**
 *  Set focus to the first invalid field
 * @param fieldName {string}
 */
export function focusOnField(fieldName) {
	const firstField = document.querySelector(
		`input[data-field-name="${fieldName}"], select[data-field-name="${fieldName}"]`
	);
	if (fieldName && firstField) {
		firstField.focus();
		if (typeof window.scrollIntoView !== "undefined") {
			window.scrollIntoView(firstField);
		}
	}
}

/**
 * Validate the value against an array of allowed patterns.
 * If any pattern matches return true. Otherwise false
 * @param {String} value
 * @param {Array[String]} allowPatterns
 */
function validateAllowPatterns(value, allowPatterns) {
	if (allowPatterns.length == 0) {
		return true;
	}
	for (let allowPattern of allowPatterns) {
		const pattern = typeof allowPattern.validationRegExpr == 'string' ? new RegExp(allowPattern.validationRegExpr, 'i') : allowPattern.validationRegExpr;
		if (pattern.test(value)) {
			return true;
		}
	}
	return false;
}

/**
 * Validate against an array of disallowed patterns.
 * If any pattern matches return false - otherwise return true
 * @param {*} value
 * @param {*} disallowPatterns
 */
function validateDisallowPatterns(value, disallowPatterns) {
	for (let disallowPattern of disallowPatterns) {
		const pattern = new RegExp(disallowPattern.validationRegExpr, 'i');
		if (pattern.test(value)) {
			return {
				result: false,
				validationErrorMessage: disallowPattern.validationErrorMessage,
			};
		}
	}
	return {
		result: true,
	};
}
